import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { AlertRow } from "../api/types";
import { alertEntityHref, alertTypeBadge, alertTypeLabel } from "../lib/alerts";
import { formatDateTime } from "../lib/workorders";

export function AlertsPage() {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);

  // The server returns saved alerts immediately and refreshes alert generation
  // in the background. The bell-badge count is on a 5-min poll, so we nudge it
  // after each list fetch so the topbar matches what the user sees here.
  const listQ = useQuery({
    queryKey: ["alerts", "list", { showAll }],
    queryFn: () => api.alerts.list(showAll),
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (listQ.dataUpdatedAt) {
      queryClient.invalidateQueries({ queryKey: ["alerts", "count"] });
    }
  }, [listQ.dataUpdatedAt, queryClient]);

  const ackMut = useMutation({
    mutationFn: (id: number) => api.alerts.acknowledge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
  const dismissMut = useMutation({
    mutationFn: (id: number) => api.alerts.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  const rows = listQ.data?.alerts ?? [];
  const groups = rows.reduce<Record<string, AlertRow[]>>((acc, alert) => {
    const key = alert.alertType;
    acc[key] = [...(acc[key] ?? []), alert];
    return acc;
  }, {});

  return (
    <Layout title="Alerts">
      <p className="help-line">
        Things that need your attention right now. Each item links to the
        matching record. Click <strong>Acknowledge</strong> or{" "}
        <strong>Dismiss</strong> to clear it from this view — it won't come
        back unless the underlying record changes (e.g. due date pushed,
        inventory drops further). Alerts also auto-clear when the issue is
        fully resolved (work order completed, inventory restocked, etc.).
      </p>

      <div className="page-actions">
        <label className="checkbox-line">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          <span>Show acknowledged + dismissed history</span>
        </label>
      </div>

      {listQ.isLoading ? (
        <p className="sub">Loading…</p>
      ) : listQ.isError ? (
        <section className="card">
          <p>Couldn't load alerts. Please try again.</p>
        </section>
      ) : rows.length === 0 ? (
        <section className="card">
          <p className="sub">
            {showAll
              ? "No alerts have ever been generated for you."
              : "No open alerts — you're caught up."}
          </p>
        </section>
      ) : (
        <section className="accordion">
          {Object.entries(groups).map(([type, alerts]) => (
            <details key={type} className="acc-item" open>
              <summary className="acc-head">
                <span className="acc-title">{alertTypeLabel(type)}</span>
                <span className={`badge badge-${alertTypeBadge(type)}`}>{alerts.length}</span>
              </summary>
              <ul className="alert-list">
                {alerts.map((a) => (
                  <li key={a.id} className={`alert-row alert-row--${alertTypeBadge(a.alertType)}`}>
                    <AlertRowView
                      alert={a}
                      onAcknowledge={() => ackMut.mutate(a.id)}
                      onDismiss={() => dismissMut.mutate(a.id)}
                    />
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </section>
      )}
    </Layout>
  );
}

function AlertRowView({
  alert,
  onAcknowledge,
  onDismiss,
}: {
  alert: AlertRow;
  onAcknowledge: () => void;
  onDismiss: () => void;
}) {
  const href = alertEntityHref(alert.relatedEntityType, alert.relatedEntityId);
  const isOpen = alert.status === "pending" || alert.status === "sent";

  return (
    <article>
      <div className="alert-row-main">
        <div>
          <div className="alert-row-head">
            <span className={`badge badge-${alertTypeBadge(alert.alertType)}`}>
              {alertTypeLabel(alert.alertType)}
            </span>
            {alert.buildingName && (
              <span className="alert-row-building">{alert.buildingName}</span>
            )}
            {!isOpen && (
              <span className="badge badge-muted">{statusLabel(alert.status)}</span>
            )}
          </div>
          <div className="alert-row-message">{alert.message}</div>
          <div className="meta">Generated {formatDateTime(alert.createdAt)}</div>
        </div>
        <div className="alert-row-actions">
          {href && (
            <Link to={href} className="btn btn-ghost btn-sm">
              Open
            </Link>
          )}
          {isOpen && (
            <>
              <button className="btn btn-primary btn-sm" onClick={onAcknowledge}>
                Acknowledge
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onDismiss}>
                Dismiss
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function statusLabel(s: string): string {
  if (s === "acknowledged") return "Acknowledged";
  if (s === "dismissed") return "Dismissed";
  if (s === "sent") return "Open";
  if (s === "pending") return "Pending";
  return s;
}
